/**
 * The scene-opening crow tour: the bird leaves its perch, flies an authored
 * route through the scene and lands back where it started, with the camera
 * trailing it.
 *
 * Pure maths, like `crowFlight` — `createScene` drives it and copies samples
 * onto the bird and the camera.
 *
 * The route is an explicit list of WAYPOINTS, not a formula. An earlier version
 * was a circle around the orbit centre with a detour spliced in, and it could
 * not express what this scene needs: the archway is a 0.48-unit gap in one
 * specific wall, and threading it means arriving on the wall's normal, which a
 * circle never does. Waypoints also make the shot directable — "along the
 * flank, up, down, through, home" is five points, not five parameters.
 *
 * Between the waypoints the path is a CENTRIPETAL Catmull-Rom (α = 0.5). Two
 * properties earn it: it provably cannot cusp or self-intersect on unevenly
 * spaced points, and its knot spacing gives long legs proportionally more time
 * than short ones — so the bird covers the open sweep quickly and slows where
 * the waypoints crowd together, which is exactly where the archway is.
 */
import { smoothstep, type Vector3Like } from './crowFlight';

export interface CrowTourParams {
  /**
   * The authored route in world coordinates. First and last must BOTH be the
   * perch — nothing else pins the bird to its landing spot.
   */
  waypoints: Vector3Like[];
  /** Total tour time in seconds. */
  duration: number;
  /** Corrects the model's forward axis, radians. */
  yawOffset: number;
  /** Vertical bob amplitude in scene units — the rise and sink of wingbeats. */
  bob: number;
  /** Bob oscillations over the whole tour. */
  bobCycles: number;
  /**
   * Roll per radian-per-unit-time of turn, so the bird banks INTO its turns
   * rather than holding a constant lean. A fixed roll reads as a model on a
   * wire; this reads as flying.
   */
  bankGain: number;
  /** Roll ceiling, radians. */
  bankLimit: number;
}

export interface CrowTourSample {
  x: number;
  y: number;
  z: number;
  /** Yaw, radians — already includes `yawOffset`. */
  heading: number;
  /**
   * Roll, radians. Positive lifts the left wing — rolls into a RIGHT turn —
   * the sign `CrowProp.applyPose` and `crowFlight` both use.
   */
  bank: number;
}

export const DEFAULT_TOUR: Omit<CrowTourParams, 'waypoints'> = {
  duration: 18,
  yawOffset: 0,
  bob: 0.018,
  // Whole cycles, so the bob is back at zero on the perch at both ends.
  bobCycles: 7,
  bankGain: 0.04,
  bankLimit: 0.7,
};

const TWO_PI = Math.PI * 2;
const EPSILON = 1e-4;

/** Step used to read heading and turn rate off the path by difference. */
const DIFF_STEP = 1e-3;

/** Ceiling on `waypoints.length`, so the knot table can be a fixed buffer. */
const MAX_WAYPOINTS = 32;

/** Knots for the waypoints plus one phantom either side. */
const KNOTS = new Float64Array(MAX_WAYPOINTS + 2);
const PHANTOM_HEAD: Vector3Like = { x: 0, y: 0, z: 0 };
const PHANTOM_TAIL: Vector3Like = { x: 0, y: 0, z: 0 };
const HERE: Vector3Like = { x: 0, y: 0, z: 0 };
const MID: Vector3Like = { x: 0, y: 0, z: 0 };
const BEFORE: Vector3Like = { x: 0, y: 0, z: 0 };
const AFTER: Vector3Like = { x: 0, y: 0, z: 0 };

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/**
 * Interpolate between two knots. Module level, and `u` is a parameter rather
 * than a captured variable, deliberately: as a closure inside `splineAxis` this
 * allocated once per axis per segment — 24 allocations on every tour frame,
 * against a render loop that is not allowed any.
 */
const knotLerp = (p: number, q: number, from: number, to: number, u: number): number =>
  to === from ? p : p + ((q - p) * (u - from)) / (to - from);

/**
 * One axis of a Catmull-Rom segment in Barry-Goldman form — repeated nested
 * lerps, so it takes the knot values rather than assuming they are evenly
 * spaced. `u` is an absolute knot value in `[k1, k2]`, not a 0-1 fraction.
 */
const splineAxis = (
  a: number,
  b: number,
  c: number,
  d: number,
  k0: number,
  k1: number,
  k2: number,
  k3: number,
  u: number,
): number => {
  const a1 = knotLerp(a, b, k0, k1, u);
  const a2 = knotLerp(b, c, k1, k2, u);
  const a3 = knotLerp(c, d, k2, k3, u);
  const b1 = knotLerp(a1, a2, k0, k2, u);
  const b2 = knotLerp(a2, a3, k1, k3, u);
  return knotLerp(b1, b2, k1, k2, u);
};

/**
 * Control point `index`, where 0 and `count + 1` are the phantoms. The phantoms
 * mirror the first and last leg, which gives the spline a tangent at the perch
 * pointing straight down the route — the bird leaves and returns along its own
 * path instead of swinging sideways off the perch.
 */
const controlPoint = (params: CrowTourParams, index: number): Vector3Like => {
  const points = params.waypoints;
  const count = points.length;
  if (index <= 0) return PHANTOM_HEAD;
  if (index >= count + 1) return PHANTOM_TAIL;
  return points[index - 1];
};

/**
 * The route the cached knots belong to, by identity. A route is never mutated
 * in place — `buildCrowTourParams` maps a fresh array — so identity is a sound
 * key, and the table is constant for the whole tour. Without this the knots were
 * rebuilt on every `positionAt`: four times per sample, 104 `Math.hypot` calls a
 * frame, for numbers that never change.
 */
let cachedRoute: Vector3Like[] | null = null;
let cachedCount = 0;

const buildKnots = (params: CrowTourParams): number => {
  const points = params.waypoints;
  if (points === cachedRoute) return cachedCount;
  const count = Math.min(points.length, MAX_WAYPOINTS);

  PHANTOM_HEAD.x = 2 * points[0].x - points[1].x;
  PHANTOM_HEAD.y = 2 * points[0].y - points[1].y;
  PHANTOM_HEAD.z = 2 * points[0].z - points[1].z;
  PHANTOM_TAIL.x = 2 * points[count - 1].x - points[count - 2].x;
  PHANTOM_TAIL.y = 2 * points[count - 1].y - points[count - 2].y;
  PHANTOM_TAIL.z = 2 * points[count - 1].z - points[count - 2].z;

  KNOTS[0] = 0;
  for (let i = 0; i <= count; i++) {
    const a = controlPoint(params, i);
    const b = controlPoint(params, i + 1);
    const chord = Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
    // √chord — the α = 0.5 exponent that makes this centripetal.
    KNOTS[i + 1] = KNOTS[i] + Math.max(Math.sqrt(chord), EPSILON);
  }
  cachedRoute = points;
  cachedCount = count;
  return count;
};

/**
 * Position at normalised time `t`, written into `out`.
 *
 * `t` is eased before it reaches the knots, so the bird accelerates off the
 * perch and settles back onto it instead of starting and stopping at cruise
 * speed. Everything between is left to the centripetal spacing.
 */
const positionAt = (params: CrowTourParams, t: number, out: Vector3Like): void => {
  const count = buildKnots(params);
  const first = KNOTS[1];
  const last = KNOTS[count];
  const u = first + smoothstep(clamp01(t)) * (last - first);

  let segment = 1;
  while (segment < count - 1 && u > KNOTS[segment + 1]) segment += 1;

  const p0 = controlPoint(params, segment - 1);
  const p1 = controlPoint(params, segment);
  const p2 = controlPoint(params, segment + 1);
  const p3 = controlPoint(params, segment + 2);
  const k0 = KNOTS[segment - 1];
  const k1 = KNOTS[segment];
  const k2 = KNOTS[segment + 1];
  const k3 = KNOTS[segment + 2];

  out.x = splineAxis(p0.x, p1.x, p2.x, p3.x, k0, k1, k2, k3, u);
  out.y = splineAxis(p0.y, p1.y, p2.y, p3.y, k0, k1, k2, k3, u);
  out.z = splineAxis(p0.z, p1.z, p2.z, p3.z, k0, k1, k2, k3, u);
};

const headingBetween = (from: Vector3Like, to: Vector3Like): number =>
  Math.atan2(to.x - from.x, to.z - from.z);

/** Shortest signed difference between two angles. */
const wrapAngle = (angle: number): number => Math.atan2(Math.sin(angle), Math.cos(angle));

/**
 * The wingbeat rise and fall added on top of the path at `u`.
 *
 * Exported because the CAMERA wants the path without it — a camera inheriting
 * the wingbeat is unwatchable. Subtracting this from a sample's `y` is the whole
 * difference between the bird's read of the route and the camera's, along with
 * `yawOffset` on the heading. Sampling the route a second time with `bob: 0` to
 * discover that costs a second full pass over the spline — four more
 * `positionAt` calls per frame — to arrive at a number two subtractions give.
 */
export const bobOffsetAt = (params: CrowTourParams, u: number): number =>
  params.bob * Math.sin(TWO_PI * params.bobCycles * clamp01(u));

/**
 * Sample the tour at normalised time `u` (0 = on the perch, 1 = back on it).
 *
 * Heading and turn rate both come from finite differences rather than analytic
 * derivatives: the path is a piecewise spline over an eased parameter, and
 * differentiating that by hand buys nothing but a chance to get it wrong.
 */
export const sampleCrowTour = (params: CrowTourParams, u: number): CrowTourSample => {
  const t = clamp01(u);
  positionAt(params, t, HERE);

  // Direction and turn rate are read off THREE distinct points, all pushed
  // inside the range. Differencing against `HERE` instead would degenerate at
  // the ends, where the clamp makes the centre point coincide with one
  // neighbour: `atan2(0, 0)` is 0, that reads as a hard turn, and the bird
  // arrives on its perch rolled hard over.
  const mid = Math.min(1 - DIFF_STEP, Math.max(DIFF_STEP, t));
  positionAt(params, mid, MID);
  positionAt(params, mid - DIFF_STEP, BEFORE);
  positionAt(params, mid + DIFF_STEP, AFTER);

  const heading = headingBetween(BEFORE, AFTER);
  // Turn rate in radians per unit of normalised time, from the two half-steps.
  // It falls to zero of its own accord at both ends, because the eased
  // parameter barely advances there — so the bird levels out to land.
  const turn = wrapAngle(headingBetween(MID, AFTER) - headingBetween(BEFORE, MID));
  // Negated: heading rises on a LEFT turn, and a positive roll lifts the left
  // wing, so the raw turn rate leans the bird out of every turn.
  const bank = Math.max(
    -params.bankLimit,
    Math.min(params.bankLimit, (-turn / (2 * DIFF_STEP)) * params.bankGain),
  );

  return {
    x: HERE.x,
    y: HERE.y + bobOffsetAt(params, t),
    z: HERE.z,
    heading: heading + params.yawOffset,
    bank,
  };
};
